import React, { ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { connectRedux } from '../../../helpers/connectRedux'
import { RouteComponentProps } from '../../../helpers/withRouter'
import Switch from 'react-switch'
import { Button, Page } from '../../../components'
import { images } from '../../../images'
import literalsService from '../../../services/literals-service'
import { changeConfirmationFlagAction, saveUserInfoAction, setNotificationAction } from '../../../store/actions'
import { IStoreState } from '../../../store/states'
import theme from '../../../theme'
import StyledArticle from './styles'
import { appsStyleSelected } from '../../../helpers/app'
import { AuthenticatedRoutable, INotificationPreference, NotificationCategory, NotificationChannel } from '../../../models'
import { UserService } from '../../../services'
import { AnyAction } from 'redux'

interface IProps extends RouteComponentProps, DispatchProp<any> {
  notificationReservationConfirmation: boolean
  isLoadingUserInfo: boolean
}

interface IState {
  preferences: INotificationPreference[]
}

class NotificationSettings extends AuthenticatedRoutable<IProps, IState> {
  public state: IState = { preferences: [] }

  public async componentDidMount() {
    super.componentDidMount()
    try {
      this.setState({ preferences: await UserService.getNotificationPreferences() })
    } catch (error) {
      this.props.dispatch(setNotificationAction({ label: literalsService.get('serverError', true), isError: true }))
    }
  }

  public render(): ReactNode {
    const { notificationReservationConfirmation } = this.props
    return (
      <Page>
        <Page className={ `authenticated ${appsStyleSelected?.style}` }>
          <StyledArticle>
            <img src={ images['goBack'] } className='backButton' onClick={ this.handleGoBack } />
            <form onSubmit={ e => e.preventDefault() }>
              <section>
                <h3>{ literalsService.get('notificationSettings', true) }</h3>
                <p className='intro'>{ literalsService.get('notificationPreferencesIntro', true) }</p>
                <section>
                  <section className='row'>
                    <span>{ literalsService.get('infoNotificationFlag', true) }</span>
                    <Switch 
                      className='react-switch'
                      onChange={this.changeInfoNotificationFlag}
                      checked={notificationReservationConfirmation}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      onHandleColor={theme.colors.primary}
                      offHandleColor={theme.colors.primary}
                      onColor={theme.colors.secondary}>
                    </Switch>
                  </section>
                  { this.groupPreferencesByCategory().map(({ category, preferences }) => (
                    <section key={ category }>
                      <h4 className='category'>{ literalsService.get(`notificationCategory_${ category }`, true) }</h4>
                      { preferences.map(this.renderPreference) }
                    </section>
                  )) }
                </section>
                <Button
                  title={ literalsService.get('save', true) }
                  onPress={ () => this.saveUserInfo() }
                  className={ 'saveButton' }
                  filled
                  upper
                />
              </section>
            </form>
          </StyledArticle>
        </Page>
      </Page>
    )
  }

  private handleGoBack = (): void => {
    history.back()
  }

  private renderPreference = (preference: INotificationPreference): ReactNode => {
    const eventLabel = literalsService.get(`notificationEvent_${ preference.event }`, true)
    const label = preference.mandatory ? `${ eventLabel } (${ literalsService.get('notificationMandatory') })` : eventLabel
    if (preference.availableChannels.length === 1) {
      return this.renderChannelSwitch(preference, preference.availableChannels[0], label)
    }
    return (
      <section key={ preference.event }>
        <span className='event'>{ label }</span>
        { preference.availableChannels.map((channel: NotificationChannel) =>
          this.renderChannelSwitch(preference, channel, literalsService.get(`notificationChannel_${ channel }`, true), 'channelRow')) }
      </section>
    )
  }

  private renderChannelSwitch(preference: INotificationPreference, channel: NotificationChannel, label: string, className = '') {
    return (
      <section className={ `row ${ className }` } key={ `${ preference.event }-${ channel }` }>
        <span>{ label }</span>
        <Switch
          className='react-switch'
          onChange={ () => this.togglePreferenceChannel(preference.event, channel) }
          checked={ !preference.mutedChannels.includes(channel) }
          disabled={ preference.mandatory }
          uncheckedIcon={ false }
          checkedIcon={ false }
          onHandleColor={ theme.colors.primary }
          offHandleColor={ theme.colors.primary }
          onColor={ theme.colors.secondary }>
        </Switch>
      </section>
    )
  }

  private groupPreferencesByCategory() {
    const { preferences } = this.state
    return Object.values(NotificationCategory)
      .map((category: NotificationCategory) => ({
        category,
        preferences: preferences.filter((preference: INotificationPreference) => preference.category === category)
      }))
      .filter(({ preferences: categoryPreferences }) => categoryPreferences.length > 0)
  }

  private togglePreferenceChannel = (event: string, channel: NotificationChannel) => {
    this.setState(({ preferences }: IState) => ({
      preferences: preferences.map((preference: INotificationPreference) => {
        if (preference.event !== event) return preference
        const isMuted = preference.mutedChannels.includes(channel)
        return {
          ...preference,
          mutedChannels: isMuted
            ? preference.mutedChannels.filter((mutedChannel: NotificationChannel) => mutedChannel !== channel)
            : [...preference.mutedChannels, channel]
        }
      })
    }))
  }

  private changeInfoNotificationFlag = () => {
    this.props.dispatch(changeConfirmationFlagAction())
  }

  private saveUserInfo = () => {
    const { dispatch } = this.props
    dispatch(saveUserInfoAction(this.state.preferences))
    dispatch(setNotificationAction({
      label: literalsService.get('notificationsChanged', true),
      isError: false
    }))
    this.handleGoBack()
  }
}

const mapStateToProps = ({ userInfo }: IStoreState) => ({
  notificationReservationConfirmation: userInfo.notificationReservationConfirmation,
  isLoadingUserInfo: userInfo.isLoadingUserInfo
})

export default connectRedux(mapStateToProps, NotificationSettings)
